import React, { useState, useEffect } from 'react';
import {
    Container,
    Card,
    Box,
    Typography,
    Avatar,
    Table,
    TableBody,
    TableContainer,
    Button,
    alpha,
    useTheme,
    useMediaQuery,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    CircularProgress
} from '@mui/material';
import {
    Person as PersonIcon,
    AccountBalance as BankIcon,
    Add as AddIcon,
    SupportAgent as SupportAgentIcon
} from '@mui/icons-material';
import { DataRow, ExchangeSubscriptions } from './components';
import { updateUserProfile } from '../../services/backendAPI';
import CountryCodePicker from '../Common/CountryCodePicker';
import useGeoCountry from '../../hooks/useGeoCountry';
import countryCodes from '../../data/countryCodes.json';
import { useDispatch } from 'react-redux';
import { updateProfileDataAction } from '../../store/actions/profile';

const AccountDetailsTab = ({ userData, onAddBankAccount }) => {
    const dispatch = useDispatch();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editField, setEditField] = useState('');
    const [editValue, setEditValue] = useState('');
    const [editError, setEditError] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [localUserData, setLocalUserData] = useState(userData);
    const { countryCode, setCountryCode } = useGeoCountry();

    useEffect(() => {
        setLocalUserData(userData);
    }, [userData]);

    if (!localUserData) return null;

    const handleEditClick = (field, currentValue) => {
        setEditField(field);
        
        let initialVal = currentValue || '';
        if (field === 'whatsapp_number' && initialVal) {
            if (initialVal.startsWith('+')) {
                const sortedCodes = [...countryCodes].sort((a, b) => b.dial_code.length - a.dial_code.length);
                const matchedCountry = sortedCodes.find(c => initialVal.startsWith(c.dial_code));
                
                if (matchedCountry) {
                    setCountryCode(matchedCountry);
                    initialVal = initialVal.substring(matchedCountry.dial_code.length);
                }
            } else if (initialVal.startsWith('91') && initialVal.length > 10) {
                // Fallback for older incorrectly formatted data without '+'
                initialVal = initialVal.substring(2);
            }
            initialVal = initialVal.replace(/\D/g, '');
        }
        
        setEditValue(initialVal);
        setEditError('');
        setEditDialogOpen(true);
    };

    const handleDialogClose = () => {
        setEditDialogOpen(false);
        setEditField('');
        setEditValue('');
        setEditError('');
    };

    const handleSave = async () => {
        if (!editValue || editValue.trim() === '') {
            setEditError('This field cannot be empty');
            return;
        }

        setIsSaving(true);
        setEditError('');
        try {
            let finalValue = editValue.trim();
            if (editField === 'whatsapp_number') {
                finalValue = (countryCode?.dial_code || '') + finalValue;
            }
            
            const payload = {
                [editField]: finalValue
            };
            const response = await updateUserProfile(payload);
            
            if (response && response.status === 'error') {
                setEditError(response.message || 'Failed to update profile. Please try again.');
                return;
            }

            dispatch(updateProfileDataAction(payload));
            setLocalUserData(prev => ({
                ...prev,
                ...payload
            }));
            handleDialogClose();
        } catch (error) {
            console.error("Failed to update profile", error);
            setEditError(typeof error === 'string' ? error : 'An unexpected error occurred.');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Container maxWidth={false} disableGutters>
            <Card sx={{
                mb: 4,
                borderRadius: { xs: 2, sm: 3 },
                overflow: 'hidden',
                border: (theme) => `1px solid ${alpha(theme.palette.text.primary, 0.2)}`,
                boxShadow: (theme) => `0 8px 32px ${alpha(theme.palette.common.black, 0.08)}`,
                transition: 'all 0.2s ease',
            }}>
                <Box sx={{
                    px: { xs: 2, sm: 3 },
                    py: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    background: theme.palette.mode === 'dark'
                        ? `linear-gradient(45deg, ${alpha(theme.palette.grey[900], 0.9)}, ${alpha(theme.palette.grey[800], 0.9)})`
                        : `linear-gradient(45deg, ${alpha(theme.palette.grey[50], 0.9)}, ${alpha(theme.palette.grey[100], 0.9)})`,
                    borderBottom: (theme) => `1px solid ${alpha(theme.palette.text.primary, 0.08)}`
                }}>
                    <Avatar
                        sx={{
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                            mr: 1.5
                        }}
                    >
                        <PersonIcon />
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '0.95rem', sm: '1.05rem' } }}>
                        Personal Information
                    </Typography>
                </Box>

                <Box sx={{ py: { xs: 1, sm: 2 } }}>
                    <TableContainer component={Box} sx={{ borderRadius: 1, overflow: 'hidden' }}>
                        <Table size={isMobile ? "small" : "medium"}>
                            <TableBody>
                                <DataRow label="Client ID" value={localUserData?.client_id} />
                                <DataRow label="Name" value={localUserData?.name} />
                                <DataRow label="Email" value={localUserData?.email_id} />
                                <DataRow label="Phone" value={localUserData?.phone_number} />
                                <DataRow 
                                    label="WhatsApp Number" 
                                    value={localUserData?.whatsapp_number} 
                                    onEdit={() => handleEditClick('whatsapp_number', localUserData?.whatsapp_number)}
                                />
                                <DataRow 
                                    label="Telegram ID" 
                                    value={localUserData?.telegram_id} 
                                    onEdit={() => handleEditClick('telegram_id', localUserData?.telegram_id)}
                                />
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </Card>

            <Card sx={{
                mb: 4,
                borderRadius: { xs: 2, sm: 3 },
                overflow: 'hidden',
                border: (theme) => `1px solid ${alpha(theme.palette.text.primary, 0.2)}`,
                boxShadow: (theme) => `0 8px 32px ${alpha(theme.palette.common.black, 0.08)}`,
                transition: 'all 0.2s ease',
            }}>
                <Box sx={{
                    px: { xs: 2, sm: 3 },
                    py: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    background: theme.palette.mode === 'dark'
                        ? `linear-gradient(45deg, ${alpha(theme.palette.grey[900], 0.9)}, ${alpha(theme.palette.grey[800], 0.9)})`
                        : `linear-gradient(45deg, ${alpha(theme.palette.grey[50], 0.9)}, ${alpha(theme.palette.grey[100], 0.9)})`,
                    borderBottom: (theme) => `1px solid ${alpha(theme.palette.text.primary, 0.08)}`
                }}>
                    <Avatar
                        sx={{
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                            mr: 1.5
                        }}
                    >
                        <SupportAgentIcon />
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '0.95rem', sm: '1.05rem' } }}>
                        Relationship Manager
                    </Typography>
                </Box>

                <Box sx={{ py: { xs: 1, sm: 2 } }}>
                    <TableContainer component={Box} sx={{ borderRadius: 1, overflow: 'hidden' }}>
                        <Table size={isMobile ? "small" : "medium"}>
                            <TableBody>
                                <DataRow label="Name" value={localUserData?.relationship_manager?.name || '-'} />
                                <DataRow label="Number" value={localUserData?.relationship_manager?.number || '-'} />
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </Card>

            {/* Exchange Subscriptions */}
            <ExchangeSubscriptions subscribedExchanges={localUserData?.exchanges_subscribed} />

            <Dialog open={editDialogOpen} onClose={handleDialogClose} fullWidth maxWidth="xs">
                <DialogTitle>Edit {editField === 'whatsapp_number' ? 'WhatsApp Number' : 'Telegram ID'}</DialogTitle>
                <DialogContent>
                    {editField === 'whatsapp_number' ? (
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mt: 1 }}>
                            <CountryCodePicker 
                                countryCode={countryCode} 
                                onChange={setCountryCode} 
                            />
                            <TextField
                                autoFocus
                                label="WhatsApp Number"
                                placeholder="Enter phone number"
                                type="text"
                                fullWidth
                                variant="outlined"
                                size="small"
                                value={editValue}
                                onChange={(e) => {
                                    const digits = e.target.value.replace(/\D/g, '');
                                    setEditValue(digits);
                                    if (editError) setEditError('');
                                }}
                                disabled={isSaving}
                                error={!!editError}
                                helperText={editError}
                                sx={{ mt: 0 }}
                                inputProps={{ inputMode: 'numeric', maxLength: 15 }}
                            />
                        </Box>
                    ) : (
                        <Box sx={{ mt: 1 }}>
                            <TextField
                                autoFocus
                                label="Telegram ID"
                                type="text"
                                fullWidth
                                variant="outlined"
                                size="small"
                                value={editValue}
                            onChange={(e) => {
                                setEditValue(e.target.value);
                                if (editError) setEditError('');
                            }}
                            disabled={isSaving}
                            error={!!editError}
                            helperText={editError}
                        />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={handleDialogClose} disabled={isSaving} color="inherit">
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleSave} 
                        disabled={isSaving || !editValue || editValue.trim() === ''} 
                        variant="contained" 
                        color="primary" 
                        disableElevation
                    >
                        {isSaving ? <CircularProgress size={24} /> : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* <Box sx={{
                mt: 4,
                p: 4,
                borderRadius: 3,
                border: `1px dashed ${alpha(theme.palette.primary.main, 0.3)}`,
                backgroundColor: alpha(theme.palette.background.paper, 0.6),
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                transition: 'all 0.2s ease',
                '&:hover': {
                    backgroundColor: alpha(theme.palette.background.paper, 0.8),
                    borderColor: theme.palette.primary.main,
                }
            }}>
                <Avatar
                    sx={{
                        width: 64,
                        height: 64,
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main,
                        mb: 2
                    }}
                >
                    <BankIcon sx={{ fontSize: 32 }} />
                </Avatar>
                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                    Add Bank Accounttt
                </Typography>
                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 3, maxWidth: 400 }}
                >
                    Add your bank account details for seamless transactions and fund transfers
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={onAddBankAccount}
                    sx={{
                        borderRadius: 2,
                        py: 1,
                        px: 3,
                        textTransform: 'none',
                        fontWeight: 600,
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
                        background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                        '&:hover': {
                            background: `linear-gradient(45deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                            boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
                        }
                    }}
                >
                    Add Bank Account
                </Button>
            </Box> */}
        </Container>
    );
};

export default AccountDetailsTab;
